import { AdminLayout } from '@/components/layouts'
import { Card } from '@/components/ui/Card'
import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'
import { Select } from '@/components/ui/Select'
import { Link, router } from '@inertiajs/react'
import {
  Search,
  Plus,
  ChevronLeft,
  ChevronRight,
  ArrowUpDown,
  Trash2,
  Mail,
  RefreshCw,
  Clock,
  CheckCircle,
  XCircle,
  AlertCircle
} from 'lucide-react'
import { useState, useCallback } from 'react'

interface AdminInvitation {
  id: number
  email: string
  status: string
  community_name: string
  invited_by_name: string
  expires_at: string
  accepted_at: string | null
  days_until_expiration: number
  expired: boolean
  created_at: string
}

interface Pagination {
  current_page: number
  per_page: number
  total_count: number
  total_pages: number
}

interface Filters {
  search: string | null
  status: string | null
  community_id: string | null
  sort: string | null
  direction: string | null
}

interface Community {
  id: number
  name: string
}

interface Stats {
  pending: number
  accepted: number
  expired: number
}

interface IndexProps {
  invitations: AdminInvitation[]
  pagination: Pagination
  filters: Filters
  communities: Community[]
  stats: Stats
}

function formatDate(dateString: string): string {
  const date = new Date(dateString)
  return date.toLocaleDateString('fr-FR', {
    day: 'numeric',
    month: 'short',
    year: 'numeric'
  })
}

function getStatusBadge(status: string, expired: boolean): { label: string; className: string; icon: typeof CheckCircle } {
  if (status === 'accepted') {
    return { label: 'Acceptée', className: 'bg-green-100 text-green-800', icon: CheckCircle }
  }
  if (status === 'expired' || expired) {
    return { label: 'Expirée', className: 'bg-red-100 text-red-800', icon: XCircle }
  }
  return { label: 'En attente', className: 'bg-yellow-100 text-yellow-800', icon: Clock }
}

export default function Index({ invitations, pagination, filters, communities, stats }: IndexProps) {
  const [search, setSearch] = useState(filters.search || '')

  const applyFilters = useCallback((newFilters: Partial<Filters>) => {
    router.get('/admin/invitations', {
      ...filters,
      ...newFilters,
      page: 1
    }, {
      preserveState: true,
      preserveScroll: true
    })
  }, [filters])

  const handleSearch = useCallback(() => {
    applyFilters({ search: search || null })
  }, [search, applyFilters])

  const handleSort = useCallback((field: string) => {
    const newDirection = filters.sort === field && filters.direction === 'desc' ? 'asc' : 'desc'
    applyFilters({ sort: field, direction: newDirection })
  }, [filters, applyFilters])

  const handlePageChange = useCallback((page: number) => {
    router.get('/admin/invitations', {
      ...filters,
      page
    }, {
      preserveState: true,
      preserveScroll: true
    })
  }, [filters])

  const handleDelete = useCallback((invitation: AdminInvitation) => {
    if (confirm(`Voulez-vous vraiment supprimer l'invitation pour ${invitation.email} ?`)) {
      router.delete(`/admin/invitations/${invitation.id}`)
    }
  }, [])

  const handleResend = useCallback((invitation: AdminInvitation) => {
    if (confirm(`Voulez-vous renvoyer l'invitation à ${invitation.email} ?`)) {
      router.post(`/admin/invitations/${invitation.id}/resend`)
    }
  }, [])

  return (
    <AdminLayout
      title="Invitations"
      breadcrumbs={[{ label: 'Invitations' }]}
    >
      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <Card className="p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-text-gray">En attente</p>
              <p className="text-2xl font-bold text-yellow-600">{stats.pending}</p>
            </div>
            <Clock size={24} className="text-yellow-600" />
          </div>
        </Card>
        <Card className="p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-text-gray">Acceptées</p>
              <p className="text-2xl font-bold text-green-600">{stats.accepted}</p>
            </div>
            <CheckCircle size={24} className="text-green-600" />
          </div>
        </Card>
        <Card className="p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-text-gray">Expirées</p>
              <p className="text-2xl font-bold text-red-600">{stats.expired}</p>
            </div>
            <XCircle size={24} className="text-red-600" />
          </div>
        </Card>
      </div>

      {/* Actions Header */}
      <div className="flex flex-col sm:flex-row justify-between gap-4 mb-6">
        <div className="flex flex-1 gap-4">
          {/* Search */}
          <div className="flex-1 max-w-md">
            <div className="flex gap-2">
              <Input
                type="text"
                placeholder="Rechercher par email..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
              />
              <Button onClick={handleSearch} variant="secondary">
                <Search size={18} />
              </Button>
            </div>
          </div>
        </div>

        <Link href="/admin/invitations/new">
          <Button className="flex items-center gap-2">
            <Plus size={18} />
            Inviter des utilisateurs
          </Button>
        </Link>
      </div>

      {/* Filters */}
      <Card className="p-4 mb-6">
        <div className="flex flex-wrap gap-4">
          <div className="w-40">
            <Select
              value={filters.status || ''}
              onChange={(e) => applyFilters({ status: e.target.value || null })}
            >
              <option value="">Tous les statuts</option>
              <option value="pending">En attente</option>
              <option value="accepted">Acceptées</option>
              <option value="expired">Expirées</option>
            </Select>
          </div>

          <div className="w-48">
            <Select
              value={filters.community_id || ''}
              onChange={(e) => applyFilters({ community_id: e.target.value || null })}
            >
              <option value="">Toutes communautés</option>
              {communities.map((community) => (
                <option key={community.id} value={community.id}>
                  {community.name}
                </option>
              ))}
            </Select>
          </div>

          {(filters.search || filters.status || filters.community_id) && (
            <Button
              variant="secondary"
              onClick={() => {
                setSearch('')
                applyFilters({
                  search: null,
                  status: null,
                  community_id: null
                })
              }}
            >
              Réinitialiser
            </Button>
          )}
        </div>
      </Card>

      {/* Results Info */}
      <div className="flex justify-between items-center mb-4">
        <p className="text-sm text-text-gray">
          {pagination.total_count} invitation{pagination.total_count > 1 ? 's' : ''}
          {pagination.total_pages > 1 && (
            <span> - Page {pagination.current_page} sur {pagination.total_pages}</span>
          )}
        </p>
      </div>

      {/* Invitations Table */}
      <Card className="overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="text-left px-4 py-3">
                  <button
                    className="flex items-center gap-1 font-medium text-text-gray hover:text-text-dark"
                    onClick={() => handleSort('email')}
                  >
                    Email
                    <ArrowUpDown size={14} />
                  </button>
                </th>
                <th className="text-left px-4 py-3">
                  <button
                    className="flex items-center gap-1 font-medium text-text-gray hover:text-text-dark"
                    onClick={() => handleSort('status')}
                  >
                    Statut
                    <ArrowUpDown size={14} />
                  </button>
                </th>
                <th className="text-left px-4 py-3 font-medium text-text-gray">
                  Communauté
                </th>
                <th className="text-left px-4 py-3 font-medium text-text-gray">
                  Invité par
                </th>
                <th className="text-left px-4 py-3">
                  <button
                    className="flex items-center gap-1 font-medium text-text-gray hover:text-text-dark"
                    onClick={() => handleSort('expires_at')}
                  >
                    Expiration
                    <ArrowUpDown size={14} />
                  </button>
                </th>
                <th className="text-left px-4 py-3 font-medium text-text-gray">
                  Créé le
                </th>
                <th className="text-right px-4 py-3 font-medium text-text-gray">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {invitations.length === 0 ? (
                <tr>
                  <td colSpan={7} className="px-4 py-8 text-center text-text-gray">
                    <div className="flex flex-col items-center gap-2">
                      <Mail size={32} className="text-gray-300" />
                      Aucune invitation trouvée
                    </div>
                  </td>
                </tr>
              ) : (
                invitations.map((invitation) => {
                  const statusBadge = getStatusBadge(invitation.status, invitation.expired)
                  const StatusIcon = statusBadge.icon
                  return (
                    <tr key={invitation.id} className="hover:bg-gray-50">
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <Mail size={16} className="text-text-gray" />
                          <span className="font-medium text-text-dark">{invitation.email}</span>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium ${statusBadge.className}`}>
                          <StatusIcon size={12} />
                          {statusBadge.label}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <span className="text-sm text-text-dark">{invitation.community_name}</span>
                      </td>
                      <td className="px-4 py-3">
                        <span className="text-sm text-text-dark">{invitation.invited_by_name}</span>
                      </td>
                      <td className="px-4 py-3">
                        {invitation.status === 'accepted' ? (
                          <span className="text-sm text-green-600">
                            Acceptée le {formatDate(invitation.accepted_at!)}
                          </span>
                        ) : invitation.expired ? (
                          <span className="text-sm text-red-600">Expirée</span>
                        ) : (
                          <div className="flex items-center gap-1">
                            {invitation.days_until_expiration <= 2 && (
                              <AlertCircle size={14} className="text-yellow-500" />
                            )}
                            <span className={`text-sm ${invitation.days_until_expiration <= 2 ? 'text-yellow-600' : 'text-text-gray'}`}>
                              {invitation.days_until_expiration} jour{invitation.days_until_expiration > 1 ? 's' : ''}
                            </span>
                          </div>
                        )}
                      </td>
                      <td className="px-4 py-3 text-sm text-text-gray">
                        {formatDate(invitation.created_at)}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center justify-end gap-2">
                          {invitation.status === 'pending' && !invitation.expired && (
                            <Button
                              variant="secondary"
                              className="p-2"
                              onClick={() => handleResend(invitation)}
                              title="Renvoyer l'invitation"
                            >
                              <RefreshCw size={16} />
                            </Button>
                          )}
                          <Button
                            variant="secondary"
                            className="p-2 text-red-600 hover:bg-red-50"
                            onClick={() => handleDelete(invitation)}
                            title="Supprimer"
                          >
                            <Trash2 size={16} />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  )
                })
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {pagination.total_pages > 1 && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-gray-200 bg-gray-50">
            <Button
              variant="secondary"
              disabled={pagination.current_page === 1}
              onClick={() => handlePageChange(pagination.current_page - 1)}
              className="flex items-center gap-1"
            >
              <ChevronLeft size={16} />
              Précédent
            </Button>

            <div className="flex items-center gap-2">
              {Array.from({ length: Math.min(5, pagination.total_pages) }, (_, i) => {
                let pageNum: number
                if (pagination.total_pages <= 5) {
                  pageNum = i + 1
                } else if (pagination.current_page <= 3) {
                  pageNum = i + 1
                } else if (pagination.current_page >= pagination.total_pages - 2) {
                  pageNum = pagination.total_pages - 4 + i
                } else {
                  pageNum = pagination.current_page - 2 + i
                }

                return (
                  <button
                    key={pageNum}
                    onClick={() => handlePageChange(pageNum)}
                    className={`w-8 h-8 rounded text-sm font-medium transition-colors ${
                      pagination.current_page === pageNum
                        ? 'bg-tessan-primary text-white'
                        : 'bg-white text-text-gray hover:bg-gray-100'
                    }`}
                  >
                    {pageNum}
                  </button>
                )
              })}
            </div>

            <Button
              variant="secondary"
              disabled={pagination.current_page === pagination.total_pages}
              onClick={() => handlePageChange(pagination.current_page + 1)}
              className="flex items-center gap-1"
            >
              Suivant
              <ChevronRight size={16} />
            </Button>
          </div>
        )}
      </Card>
    </AdminLayout>
  )
}
